import { Gallery } from '@core/Gallery/Gallery';
import { Accordion, Grid, Paginator } from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import { useMemo } from 'react';
import * as React from 'react';
import { SearchPageBase } from '@common/SearchPageBase';
import {
  DEFAULT_TABLE_PAGE_SIZE,
  DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
  EVENT_NAME,
  LOCAL_STORAGE_KEYS,
} from '@common/constants';
import { IGalleryItem } from '@core/Gallery/gallery.models';
import { convertISOStringToLocalDateString } from '@core/date.services';
import { IAnnouncementMediaField } from './announcement.models';
import { EAnnouncementFieldType } from './announcement.constants';
import './Announcement.scss';
import { useFetchAnnouncementSearch } from './announcement.queries';

export const AnnouncementList = () => {
  const { navigate } = useNavigate();
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_TABLE_PAGE_SIZE);

  const getValidPageIndex = (pageIdx: number, totalPageCount: number): number => {
    if (pageIdx <= 1) return 1;
    if (pageIdx > totalPageCount) return totalPageCount;
    return pageIdx;
  };
  const {
    data: dataAnnouncement,
    isLoading: isLoadingAnnouncement,
    isRefetching: isRefetchingAnnouncement,
  } = useFetchAnnouncementSearch(
    {
      pageSize,
      pageNumber,
    },
    {},
  );

  const filteredAnnouncement = useMemo(() => {
    const filtered = dataAnnouncement?.items?.filter((a) => !a.isExpired);
    return filtered;
  }, [JSON.stringify(dataAnnouncement)]);

  const [readAnnouncement, setReadAnnouncement] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    const record = new Set(
      (localStorage.getItem(LOCAL_STORAGE_KEYS.ANNOUNCEMENT_READ) ?? '').split(',').map((i) => Number.parseInt(i, 10)),
    );

    setReadAnnouncement((old) => {
      return new Set([...old, ...record]);
    });
  }, []);

  React.useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.ANNOUNCEMENT_READ, Array.from(readAnnouncement).join(','));
    window.dispatchEvent(new Event(EVENT_NAME.SET_LOCAL_STORAGE));
  }, [readAnnouncement]);

  const onExpandAccordion = (id: number, isExpand: boolean) => {
    if (!isExpand) return;
    setReadAnnouncement((old) => {
      return new Set([...old]).add(id);
    });
  };

  return (
    <SearchPageBase
      isGlobalPage
      isTableLoading={isLoadingAnnouncement}
      isTableRefetching={isRefetchingAnnouncement}
      data={filteredAnnouncement}
      title="Announcement"
      breadcrumbItems={[
        {
          text: 'Home',
          prefixIconName: 'House',
          onClick: () => {
            navigate(routes.announcement.list.path);
          },
        },
        {
          text: `Announcement`,
          prefixIconName: 'Megaphone',
          active: true,
        },
      ]}
      onRenderTable={() => {
        return (
          <>
            <Paginator
              totalCount={dataAnnouncement?.totalCount ?? 0}
              toPage={(pageIndex: number) => setPageNumber(pageIndex + 1)}
              changePageSize={(newSize) => setPageSize(newSize)}
              maxPrevOptions={2}
              maxNextOptions={2}
              pageSizeOptions={DEFAULT_TABLE_PAGE_SIZE_OPTIONS}
              pageIndex={pageNumber - 1}
              pageSize={pageSize}
              pageCount={dataAnnouncement?.totalPage ?? 0}
              resetPageIndex={() => setPageNumber(1)}
              toNextPage={() => setPageNumber((old) => getValidPageIndex(old + 1, dataAnnouncement?.totalPage ?? 0))}
              toPrevPage={() => setPageNumber((old) => getValidPageIndex(old - 1, dataAnnouncement?.totalPage ?? 0))}
              hasPrevPage={pageNumber > 1}
              hasNextPage={pageNumber < (dataAnnouncement?.totalPage ?? 0)}
              isValidPage={(toPageIndex: number) =>
                toPageIndex >= 0 && toPageIndex < (dataAnnouncement?.totalPage ?? 0)
              }
            />
            <Accordion
              allowClickHeaderToExpand
              content={(filteredAnnouncement ?? []).map((announcement, index) => {
                const title =
                  (announcement.fields.find((a) => a.fieldType === EAnnouncementFieldType.TITLE)?.data as string) ?? '';

                return {
                  defaultIsExpanded: false,
                  id: announcement.id,
                  onExpand: (isExpand: boolean) => {
                    onExpandAccordion(announcement.id, isExpand);
                  },
                  onRenderBody: (isExpanded: boolean) => {
                    return (
                      <>
                        {announcement.fields.map((field) => {
                          switch (field.fieldType) {
                            case EAnnouncementFieldType.TEXT:
                              return <div className="gcl-mb-xs gcl-fb-lg">{field.data as string}</div>;

                            case EAnnouncementFieldType.MEDIA: {
                              const tempField = field as IAnnouncementMediaField;
                              const images = tempField.data ?? [];
                              const galleryItems: IGalleryItem[] = images.map((i) => {
                                return {
                                  imageSource: i.url,
                                  itemId: announcement.id,
                                };
                              });
                              return (
                                <Gallery
                                  isExpanded={isExpanded}
                                  items={galleryItems}
                                  id={`announcement-list-gallery-${announcement.id}`}
                                  className="gcl-mb-xs"
                                />
                              );
                            }

                            default:
                              return <></>;
                          }
                        })}
                      </>
                    );
                  },
                  onRenderHeader: (isExpanded: boolean) => {
                    const isUnread = !readAnnouncement.has(announcement.id);
                    return (
                      <Grid className="gcl-ft-xl" display="flex" alignItems="center">
                        {isUnread && <span className="gcl-gems-announcement-list-unread-dot" />}
                        <span className="gcl-fb-sm gcl-fc-greyscale-600 gcl-pr-md">
                          {convertISOStringToLocalDateString(announcement?.announcementDate)}
                        </span>
                        <span>{title}</span>
                      </Grid>
                    );
                  },
                };
              })}
            />
          </>
        );
      }}
    />
  );
};
